import { useCustomNavigate } from '@hooks/useCustomNavigate'
import { Modal, Text } from '@shopify/polaris'
import { useAuthStore } from '@store/authStore'
import { resolveAuthFailureInstallUrl } from '@utils/shopifyAuthFailure'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const AUTH_REDIRECT_TIMEOUT_MS = 15_000

export const AuthFailureModal = () => {
  const customNavigate = useCustomNavigate()
  const authFailure = useAuthStore(state => state.authFailure)
  const clearAuthFailure = useAuthStore(state => state.clearAuthFailure)
  const [isAuthRedirecting, setIsAuthRedirecting] = useState(false)
  const [authRedirectTimedOut, setAuthRedirectTimedOut] = useState(false)
  const authRedirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  const clearAuthRedirectTimeout = useCallback(() => {
    if (authRedirectTimeoutRef.current) {
      clearTimeout(authRedirectTimeoutRef.current)
      authRedirectTimeoutRef.current = null
    }
  }, [])

  const startAuthRedirect = () => {
    setAuthRedirectTimedOut(false)
    setIsAuthRedirecting(true)
    clearAuthRedirectTimeout()
    authRedirectTimeoutRef.current = setTimeout(() => {
      setIsAuthRedirecting(false)
      setAuthRedirectTimedOut(true)
      authRedirectTimeoutRef.current = null
    }, AUTH_REDIRECT_TIMEOUT_MS)
  }

  const handleOpenInstall = () => {
    if (isAuthRedirecting) {
      return
    }

    const target = resolveAuthFailureInstallUrl(
      authFailure?.reauthStatus,
      authFailure?.installUrl,
    )
    if (!target) {
      return
    }

    startAuthRedirect()
    if (!authFailure?.installUrl) {
      clearAuthFailure()
    }

    customNavigate({
      url: target,
      target: 'self',
    })
  }

  const handleExitToAdmin = () => {
    if (isAuthRedirecting) {
      return
    }

    const target = authFailure?.adminHomeUrl || 'https://admin.shopify.com/'
    clearAuthFailure()
    customNavigate({
      url: target,
      target: 'self',
    })
  }

  const handleDismissPermissionModal = () => {
    if (isAuthRedirecting) {
      return
    }

    const onDismiss = authFailure?.onDismiss
    clearAuthFailure()
    onDismiss?.()
  }

  useEffect(() => {
    return () => clearAuthRedirectTimeout()
  }, [clearAuthRedirectTimeout])

  useEffect(() => {
    if (!authFailure) {
      setIsAuthRedirecting(false)
      setAuthRedirectTimedOut(false)
      clearAuthRedirectTimeout()
    }
  }, [authFailure, clearAuthRedirectTimeout])

  const reauthStatus = authFailure?.reauthStatus ?? null

  const authFailureContent = useMemo(() => {
    if (reauthStatus === 'session_expired') {
      return {
        title: 'Session expired',
        message:
          'Your Shopify session has expired. Please reauthenticate to continue using the app.',
        primaryAction: 'Reauthenticate',
      }
    }

    if (reauthStatus === 'reauth_required') {
      return {
        title: 'Additional permissions required',
        message:
          'This action requires additional Shopify permissions. Grant access to continue.',
        primaryAction: 'Grant permissions',
      }
    }

    return {
      title: 'Session expired',
      message:
        'Your Shopify session has expired. Please reauthenticate to continue using the app.',
      primaryAction: 'Reauthenticate',
    }
  }, [reauthStatus])

  return (
    <Modal
      open={!!authFailure}
      onClose={
        isAuthRedirecting
          ? () => {}
          : reauthStatus === 'reauth_required'
            ? handleDismissPermissionModal
            : handleOpenInstall
      }
      title={authFailureContent.title}
      primaryAction={{
        content: authRedirectTimedOut
          ? 'Retry'
          : authFailureContent.primaryAction,
        onAction: handleOpenInstall,
        loading: isAuthRedirecting,
        disabled: isAuthRedirecting,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction:
            reauthStatus === 'reauth_required'
              ? handleDismissPermissionModal
              : handleExitToAdmin,
          disabled: isAuthRedirecting,
        },
      ]}
    >
      <Modal.Section>
        <Text as="p">{authFailureContent.message}</Text>
        {authRedirectTimedOut ? (
          <Text as="p" tone="critical">
            Redirect is taking longer than expected. Please try again or cancel
            and check your connection.
          </Text>
        ) : null}
      </Modal.Section>
    </Modal>
  )
}
